import { useTranslation } from 'react-i18next';

import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Label } from '@nucleus-commerce/ui/components/label';

import { canGrant, permissionGroups } from '../schemas/role-rules';

export interface PermissionChecklistProps {
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** What the signed-in person holds; anything else is shown but cannot be ticked. */
  held: readonly string[];
  disabled?: boolean;
  /** Distinguishes the checkbox ids when several checklists share a page. */
  idPrefix?: string;
  /** Only these permissions are listed (removals offer what the role holds); groups left empty are hidden. */
  only?: readonly string[];
}

/** Grouped permission checkboxes; ones the editor lacks stay visible, greyed, with the reason. */
export function PermissionChecklist({
  value,
  onChange,
  held,
  disabled,
  idPrefix = 'permission',
  only,
}: PermissionChecklistProps) {
  const { t } = useTranslation('team');
  const selected = new Set(value);

  const toggle = (permission: string, on: boolean) =>
    onChange(on ? [...value, permission] : value.filter((p) => p !== permission));

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {permissionGroups()
        .map((group) => ({
          ...group,
          permissions: only ? group.permissions.filter((p) => only.includes(p)) : group.permissions,
        }))
        .filter((group) => group.permissions.length > 0)
        .map((group) => (
          <fieldset key={group.key} className="rounded-md border p-3" disabled={disabled}>
            <legend className="px-1 text-sm font-medium">{t(`roles.groups.${group.key}`)}</legend>
            <ul className="flex flex-col gap-1.5">
              {group.permissions.map((permission) => {
                const id = `${idPrefix}-${permission}`;
                const grantable = canGrant(held, permission);
                return (
                  <li key={permission} className="flex items-start gap-2">
                    <Checkbox
                      id={id}
                      checked={selected.has(permission)}
                      disabled={!grantable && !selected.has(permission)}
                      onCheckedChange={(next) => toggle(permission, next === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor={id} className="flex flex-col items-start gap-0 font-normal">
                      <span>{t(`roles.permissions.${permission.replace('.', '_')}`)}</span>
                      <span className="text-muted-foreground font-mono text-[11px]">
                        {permission}
                        {grantable ? null : ` · ${t('roles.notYours')}`}
                      </span>
                    </Label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))}
    </div>
  );
}
